import segmentPlugin from '@analytics/segment'
import Analytics from 'analytics'

import { AnalyticsAction, AnalyticsActionType, AnalyticsPageAction, AnalyticsTrackAction } from 'types/analytics.type'
import { excludeFalsy, isString } from 'utils/common'

export const analytics = Analytics({
  app: 'tccc-landing',
  plugins: [
    segmentPlugin({
      //   writeKey: process.env.REACT_APP_ANALYTICS_SEGMENT_TOKEN,
      writeKey: process.env.REACT_APP_SEGMENT_ANALYTICS_KEY,
    }),
  ].filter(excludeFalsy),
})

export const reset = () => {
  analytics.reset()
}

const getAnalyticsTenantAzId = () => analytics?.getState()?.user?.traits?.tenantAzId

const trackPage = ({ payload }: AnalyticsPageAction) => {
  const analyticsTenantAzId = getAnalyticsTenantAzId()
  if (analyticsTenantAzId) {
    analytics.page(
      {
        title: payload,
      },
      {
        agency: analyticsTenantAzId,
      },
    )
  }
}

const trackAction = ({ payload }: AnalyticsTrackAction) => {
  const analyticsTenantAzId = getAnalyticsTenantAzId()
  if (analyticsTenantAzId) {
    if (isString(payload)) {
      analytics.track(payload, {
        agency: analyticsTenantAzId,
      })
    } else {
      const { action, params } = payload

      analytics.track(action, {
        agency: analyticsTenantAzId,
        ...Object.fromEntries(params.map(({ key, value }) => [key, value])),
      })
    }
  }
}

export const trackAnalytics = (action: AnalyticsAction) => {
  switch (action.type) {
    case AnalyticsActionType.page:
      trackPage(action)
      break

    case AnalyticsActionType.action:
      trackAction(action)
      break

    default:
      break
  }
}

export const EVENTS = {
  PLAYGROUND_HOMEPAGE: 'pg_pv_homepage',

  PLAYGROUND_OPEN_PLAYLIST: 'pg_act_homepage_header_openplaylist',
  PLAYGROUND_HIDE_SUMMARY: 'pg_act_homepage_projecthealth_hideprojectsummary',
  PLAYGROUND_SHOW_SUMMARY: 'pg_act_homepage_showprojectsummary',
  PLAYGROUND_NOTIFY_PERSON: 'pg_act_homepage_projecthealth_latetasks_notifyperson',
  PLAYGROUND_VIEW_RISK: 'pg_act_homepage_projecthealth_atriskprojects_viewrisk',
  PLAYGROUND_VIEW_ASSIGNMENTS: 'pg_act_homepage_header_dailycount_viewassignments',
  OPEN_PROJECTS: {
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_CLOSE: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_close`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_OPENWATCHVIDEO: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_openwatchvideoaccordion`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_BACK: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_back`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_SHAREPROJECT: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_shareproject`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_COPYLINK: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_copylink`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_SHOWONLYFAVORITEPROJECTS: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_showonlyfavoriteprojects`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_HOWCANIPARTICIPATE: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_howcaniparticipate`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P1_OPENWATCHVIDEO_PLAYVIDEO: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page1_openwatchvideoaccordion_playvideo`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_CLOSE: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_close`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_CORETEAM_RAISEYOURHAND: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_coreteam_raiseyourhand`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_CORETEAM_EXPSLIDER: (projectName: string, experience: number) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_coreteam_${experience}_slider`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_EXTENDEDTEAM_RAISEYOURHAND: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_extendedteam_raiseyourhand`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_EXTENDEDTEAM_EXPSLIDER: (projectName: string, experience: number) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_extendedteam_${experience}_slider`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_BACK: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_back`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_SHAREPROJECT: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_shareproject`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_COPYLINK: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_copylink`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_SHOWONLYFAVORITEPROJECTS: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_showonlyfavoriteprojects`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P2_CONFIRM: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page2_confirm`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P3_BACK: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page3_back`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P3_SHAREPROJECT: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page3_shareproject`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P3_COPYLINK: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page3_copylink`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P3_SHOWONLYFAVORITEPROJECTS: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page3_showonlyfavoriteprojects`,
    OPENPROJECTS_VIEWPROJECTDETAILS_P3_CONFIRM: (projectName: string) =>
      `pg_act_openprojects_${projectName}_viewprojectdetails_page3_confirm`,
    OPENPROJECTS_FAVORITEPROJECT: (projectName: string) => `pg_act_openprojects_${projectName}_favourite`,
    OPENPROJECTS_VIEWPROJECTDETAILS: (projectName: string) => `pg_act_openprojects_${projectName}_viewprojectdetails`,
    PAGE_OPENPROJECTS_VIEWPROJECTDETAILS_P1: (projectName: string) =>
      `pg_pv_openprojects_${projectName}_viewprojectdetails_page1`,
    PAGE_OPENPROJECTS_VIEWPROJECTDETAILS_P2: (projectName: string) =>
      `pg_pv_openprojects_${projectName}_viewprojectdetails_page2`,
    PAGE_OPENPROJECTS_VIEWPROJECTDETAILS_P3: (projectName: string) =>
      `pg_pv_openprojects_${projectName}_viewprojectdetails_page3`,
  },
}

// TODO configure this function to
export const getEventPayload = (event: string, itemName: string) => event.replace('{itemName}', itemName)
